import React, { useState } from 'react'
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import SaveIcon from '@mui/icons-material/Save'
import { mainGreen } from '../../../utils/color'
import {
	accountTypeLabel,
	categoryAllowedSources,
	EXPENSE_SOURCES,
	expenseSourceLabel,
	isReservedExpenseCategory,
	sectionSx,
} from '../cashflowUtils'
import { EmptyRow } from './CashflowCommon'

const orderLinkModeValue = (categoryForm) => {
	if (!categoryForm.allowOrderSelection) return 'none'
	return categoryForm.allowDuplicateOrders ? 'duplicate' : 'unique'
}

const orderLinkModeLabel = (category) => {
	if (category.type === 'shipping') return 'Bắt buộc chọn đơn'
	if (category.allowOrderSelection === false) return 'Không chọn đơn'
	return 'Chọn đơn'
}

const duplicateModeLabel = (category) => {
	if (category.type === 'shipping') return 'Không'
	if (category.allowOrderSelection === false) return 'Không áp dụng'
	return category.allowDuplicateOrders === false ? 'Không' : 'Có'
}

const approvalModeLabel = (category) => (category.requiresApproval ? 'Có' : 'Không')

const sourceModeLabel = (category) => categoryAllowedSources(category).map(expenseSourceLabel).join(' + ')

const toggleAllowedSource = (allowedSources, source) => {
	const current = allowedSources || []
	const next = current.includes(source) ? current.filter((item) => item !== source) : [...current, source]
	return EXPENSE_SOURCES.filter((item) => next.includes(item))
}

const emptyBankForm = {
	name: '',
	accountType: 'bank',
	bankName: '',
	accountName: '',
	accountNumber: '',
	note: '',
}

export default function ConfigTab({
	bankAccounts,
	bankForm,
	canManageAccounts,
	canManageConfig,
	categoryDialogOpen,
	categoryForm,
	closeCategoryDialog,
	createDefaultExpenseCategories,
	deleteBankAccount,
	deleteExpenseCategory,
	editingCategoryId,
	expenseCategories,
	hasDefaultExpenseCategory,
	openCreateExpenseCategory,
	saveBankAccount,
	saveExpenseCategory,
	section = 'all',
	setBankAccountActive,
	setBankForm,
	setCategoryForm,
	startEditExpenseCategory,
}) {
	const [bankDialogOpen, setBankDialogOpen] = useState(false)

	const openBankDialog = () => {
		setBankForm(emptyBankForm)
		setBankDialogOpen(true)
	}

	const closeBankDialog = () => {
		setBankDialogOpen(false)
		setBankForm(emptyBankForm)
	}

	const handleSaveBankAccount = async () => {
		const saved = await saveBankAccount()
		if (saved) {
			setBankDialogOpen(false)
		}
	}

	const showAccounts = section === 'all' || section === 'accounts'
	const showCategories = section === 'all' || section === 'categories'

	const renderAccountConfig = () => (
		<Paper elevation={0} sx={sectionSx}>
			<Dialog
				open={bankDialogOpen}
				onClose={closeBankDialog}
				fullWidth
				maxWidth='sm'
				PaperProps={{ className: 'cashflow-dialog-paper' }}>
				<DialogTitle>Tạo tài khoản ngân hàng</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={1.75} sx={{ pt: 0.5 }}>
						<TextField
							fullWidth
							size='small'
							label='Tên hiển thị'
							value={bankForm.name}
							onChange={(event) => setBankForm((prev) => ({ ...prev, name: event.target.value }))}
						/>
						<TextField
							fullWidth
							size='small'
							label='Ngân hàng'
							value={bankForm.bankName}
							onChange={(event) => setBankForm((prev) => ({ ...prev, bankName: event.target.value }))}
						/>
						<TextField
							fullWidth
							size='small'
							label='Số tài khoản'
							value={bankForm.accountNumber}
							onChange={(event) => setBankForm((prev) => ({ ...prev, accountNumber: event.target.value }))}
						/>
						<TextField
							fullWidth
							size='small'
							label='Chủ tài khoản'
							value={bankForm.accountName}
							onChange={(event) => setBankForm((prev) => ({ ...prev, accountName: event.target.value }))}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button startIcon={<CloseIcon />} onClick={closeBankDialog}>
						Hủy
					</Button>
					<Button
						variant='contained'
						startIcon={<SaveIcon />}
						sx={{ background: mainGreen }}
						onClick={handleSaveBankAccount}>
						Tạo tài khoản ngân hàng
					</Button>
				</DialogActions>
			</Dialog>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={1}
				alignItems={{ xs: 'stretch', sm: 'center' }}
				justifyContent='space-between'
				sx={{ mb: 2 }}>
				<Stack direction='row' spacing={1} alignItems='center'>
					<AccountBalanceIcon sx={{ color: mainGreen }} />
					<Typography sx={{ fontWeight: 800 }}>Tài khoản ngân hàng</Typography>
				</Stack>
				{canManageAccounts && (
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
						<Button variant='contained' startIcon={<AddIcon />} sx={{ background: mainGreen }} onClick={openBankDialog}>
							Thêm tài khoản
						</Button>
					</Stack>
				)}
			</Stack>
			<Box className='cashflow-table-shell cashflow-scroll'>
				<Table size='small' sx={{ minWidth: 760 }}>
					<TableHead>
						<TableRow>
							<TableCell>Tên</TableCell>
							<TableCell>Loại</TableCell>
							<TableCell>Thông tin</TableCell>
							<TableCell align='right'></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{bankAccounts.length === 0 && <EmptyRow colSpan={4} text='Chưa có tài khoản ngân hàng' />}
						{bankAccounts.map((account) => {
							const isPaused = account.active === false
							return (
								<TableRow key={account._id} sx={isPaused ? { background: '#FAFAFA', color: '#8A9A93' } : undefined}>
									<TableCell sx={{ fontWeight: 800 }}>
										<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
											<Typography sx={{ fontWeight: 800, color: isPaused ? '#8A9A93' : 'inherit' }}>
												{account.name}
											</Typography>
											{isPaused && <Chip size='small' color='warning' label='Tạm ngưng' sx={{ height: 22 }} />}
										</Stack>
									</TableCell>
									<TableCell>
										<Chip size='small' label={accountTypeLabel(account)} />
									</TableCell>
									<TableCell>
										{(account.accountType || 'bank') === 'cash' ? (
											<Typography sx={{ fontSize: 13, color: '#637381' }}>Quỹ tiền mặt tại cửa hàng</Typography>
										) : (account.accountType || 'bank') === 'app' ? (
											<>
												{account.bankName || 'Tài khoản app'} {account.accountNumber && `- ${account.accountNumber}`}
												{account.accountName && <Typography sx={{ fontSize: 12 }}>{account.accountName}</Typography>}
											</>
										) : (
											<>
												{account.bankName} {account.accountNumber && `- ${account.accountNumber}`}
												{account.accountName && <Typography sx={{ fontSize: 12 }}>{account.accountName}</Typography>}
											</>
										)}
									</TableCell>
									<TableCell align='right'>
										{canManageAccounts && (
											<Stack direction='row' spacing={0.5} alignItems='center' justifyContent='flex-end'>
												<Button
													size='small'
													variant='outlined'
													color={isPaused ? 'success' : 'warning'}
													startIcon={isPaused ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />}
													sx={{ whiteSpace: 'nowrap' }}
													onClick={() => setBankAccountActive(account._id, isPaused)}>
													{isPaused ? 'Dùng lại' : 'Tạm ngưng dùng'}
												</Button>
												<Tooltip title='Xoá hẳn tài khoản'>
													<IconButton
														aria-label='Xoá tài khoản'
														size='small'
														color='error'
														onClick={() => deleteBankAccount(account._id)}>
														<DeleteIcon fontSize='small' />
													</IconButton>
												</Tooltip>
											</Stack>
										)}
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</Box>
		</Paper>
	)

	const renderExpenseCategoryConfig = () => (
		<Paper elevation={0} sx={sectionSx}>
			<Dialog open={categoryDialogOpen} onClose={closeCategoryDialog} fullWidth maxWidth='sm'>
				<DialogTitle>{editingCategoryId ? 'Sửa khoản chi' : 'Tạo khoản chi'}</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={2} sx={{ pt: 0.5 }}>
						<TextField
							fullWidth
							size='small'
							label='Tên khoản chi'
							value={categoryForm.name}
							onChange={(event) => setCategoryForm((prev) => ({ ...prev, name: event.target.value }))}
						/>
						<FormControl fullWidth size='small'>
							<InputLabel>Liên kết đơn</InputLabel>
							<Select
								label='Liên kết đơn'
								value={orderLinkModeValue(categoryForm)}
								onChange={(event) => {
									const mode = event.target.value
									setCategoryForm((prev) => ({
										...prev,
										allowOrderSelection: mode !== 'none',
										allowDuplicateOrders: mode === 'duplicate',
									}))
								}}>
								<MenuItem value='none'>Không chọn đơn</MenuItem>
								<MenuItem value='unique'>Chọn đơn - không trùng</MenuItem>
								<MenuItem value='duplicate'>Chọn đơn - cho trùng</MenuItem>
							</Select>
						</FormControl>
						<Box>
							<Typography sx={{ color: '#637381', fontSize: 12, fontWeight: 700, mb: 0.5 }}>
								Nguồn tiền (chọn 1 hoặc cả 2)
							</Typography>
							<Stack direction='row' spacing={1}>
								{EXPENSE_SOURCES.map((source) => {
									const selected = (categoryForm.allowedSources || []).includes(source)
									return (
										<Chip
											key={source}
											clickable
											label={expenseSourceLabel(source)}
											color={selected ? 'success' : 'default'}
											variant={selected ? 'filled' : 'outlined'}
											icon={selected ? <CheckIcon /> : undefined}
											onClick={() =>
												setCategoryForm((prev) => ({
													...prev,
													allowedSources: toggleAllowedSource(prev.allowedSources, source),
												}))
											}
											sx={{ fontWeight: 800 }}
										/>
									)
								})}
							</Stack>
							{(categoryForm.allowedSources || []).length === 0 && (
								<Typography sx={{ color: '#D32F2F', fontSize: 12, fontWeight: 700, mt: 0.5 }}>
									Phải chọn ít nhất 1 nguồn tiền
								</Typography>
							)}
						</Box>
						<FormControl fullWidth size='small'>
							<InputLabel>Xác nhận</InputLabel>
							<Select
								label='Xác nhận'
								value={categoryForm.requiresApproval ? 'yes' : 'no'}
								onChange={(event) =>
									setCategoryForm((prev) => ({ ...prev, requiresApproval: event.target.value === 'yes' }))
								}>
								<MenuItem value='no'>Không</MenuItem>
								<MenuItem value='yes'>Có</MenuItem>
							</Select>
						</FormControl>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button startIcon={<CloseIcon />} onClick={closeCategoryDialog}>
						Hủy
					</Button>
					<Button
						variant='contained'
						startIcon={<SaveIcon />}
						sx={{ background: mainGreen }}
						onClick={saveExpenseCategory}>
						{editingCategoryId ? 'Lưu khoản chi' : 'Tạo khoản chi'}
					</Button>
				</DialogActions>
			</Dialog>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={1}
				alignItems={{ xs: 'stretch', sm: 'center' }}
				justifyContent='space-between'
				sx={{ mb: 2 }}>
				<Stack direction='row' spacing={1} alignItems='center'>
					<RequestQuoteIcon sx={{ color: mainGreen }} />
					<Typography sx={{ fontWeight: 800 }}>Danh sách khoản chi</Typography>
				</Stack>
				{canManageConfig && (
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
						{!hasDefaultExpenseCategory && (
							<Button variant='outlined' startIcon={<AddIcon />} onClick={createDefaultExpenseCategories}>
								Tạo mặc định
							</Button>
						)}
						<Button
							variant='contained'
							startIcon={<AddIcon />}
							sx={{ background: mainGreen }}
							onClick={openCreateExpenseCategory}>
							Thêm khoản chi
						</Button>
					</Stack>
				)}
			</Stack>
			<Box className='cashflow-table-shell cashflow-scroll'>
				<Table size='small' sx={{ minWidth: 760 }}>
					<TableHead>
						<TableRow>
							<TableCell>Tên khoản chi</TableCell>
							<TableCell>Nguồn tiền</TableCell>
							<TableCell>Chọn đơn</TableCell>
							<TableCell>Trùng đơn</TableCell>
							<TableCell>Xác nhận</TableCell>
							<TableCell align='right'></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{expenseCategories.length === 0 && <EmptyRow colSpan={6} text='Chưa có khoản chi cấu hình' />}
						{expenseCategories.map((category) => (
							<TableRow key={category._id} selected={editingCategoryId === category._id}>
								<TableCell sx={{ fontWeight: 700 }}>
									<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
										<Typography sx={{ fontWeight: 800 }}>{category.name}</Typography>
										{isReservedExpenseCategory(category) && (
											<Chip
												size='small'
												label='Mặc định'
												sx={{ height: 22, background: '#E8F5E9', color: mainGreen }}
											/>
										)}
									</Stack>
								</TableCell>
								<TableCell>
									<Chip size='small' label={sourceModeLabel(category)} />
								</TableCell>
								<TableCell>
									<Chip
										size='small'
										label={orderLinkModeLabel(category)}
										color={category.allowOrderSelection === false ? 'default' : 'success'}
									/>
								</TableCell>
								<TableCell>
									<Chip
										size='small'
										label={duplicateModeLabel(category)}
										color={
											category.type === 'shipping' ||
											(category.allowOrderSelection !== false && category.allowDuplicateOrders === false)
												? 'warning'
												: 'default'
										}
									/>
								</TableCell>
								<TableCell>
									<Chip
										size='small'
										label={approvalModeLabel(category)}
										color={category.requiresApproval ? 'warning' : 'default'}
									/>
								</TableCell>
								<TableCell align='right'>
									{canManageConfig && !isReservedExpenseCategory(category) && (
										<Stack direction='row' spacing={0.5} justifyContent='flex-end'>
											<IconButton color='primary' size='small' onClick={() => startEditExpenseCategory(category)}>
												<EditIcon fontSize='small' />
											</IconButton>
											<IconButton color='error' size='small' onClick={() => deleteExpenseCategory(category._id)}>
												<DeleteIcon fontSize='small' />
											</IconButton>
										</Stack>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Box>
		</Paper>
	)

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
			{showAccounts && renderAccountConfig()}
			{showCategories && renderExpenseCategoryConfig()}
		</Box>
	)
}
